import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectorRef  } from "@angular/core";
import PipelineConfigurationService from '../pipeline-configuration.service';

// This component is a sub-component of the overall Metric Definition component.  It handles the formula for calculated metrics.
@Component({
    selector: "metric-formula",
    templateUrl: './metric-formula.component.html',
    styleUrls: ['./metric-formula.component.scss']
})
export class MetricFormulaComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    initialFormula: string = "";

    @Input() rootMetricID: number;

    // This event is emitted when the formula changes AND a validation check has happened.
    @Output()
    formulaChange: EventEmitter<FormulaState> = new EventEmitter<FormulaState>();

    @ViewChild("metricSelectorContainer")
    metricSelectorContainer: ElementRef<HTMLDivElement>;

    @ViewChild("formulaTextArea")
    formulaTextArea: ElementRef<HTMLTextAreaElement>;

    metricSelectorLoading: boolean = true;
    isRootMetricBeingAddedToFormula = false;
    formulaString: string = "";
    private isValid: boolean = true;

    private registeredMetricClickedHandler: (MetricSelectorMetricClickedEvent) => void;

    constructor(
        private service: PipelineConfigurationService,
        private cdr: ChangeDetectorRef,
    ) { }

    /**
     * Reads the attribute "initial-formula-string" passed in by the parent directive.
     */
    ngOnInit() {
        if (typeof this.initialFormula === "string" && this.initialFormula.length > 0) {
            this.formulaString = this.initialFormula;
            this.revalidate();
        }
    }

    /**
     * Registers the handler for custom jQuery event "metricClicked" from the Metric Selector.
     */
    async ngAfterViewInit() {
        let metricSelector = await this.service.getMetricFormulaMetricSelectorHtml();
        this.metricSelectorContainer.nativeElement.innerHTML = metricSelector;
        this.metricSelectorLoading = false;

        this.registeredMetricClickedHandler = this.addMetric.bind(this);
        $(document).on("metricClicked", this.registeredMetricClickedHandler);
    }

    /**
     * De-registers the handler for the custom jQuery event "metricClicked" from the Metric Selector.
     */
    ngOnDestroy() {
        $(document).off("metricClicked", this.registeredMetricClickedHandler);
    }

    /**
     * Adds the clicked metric's MetricID from the metric selector to the current cursor position in the formula.
     * @param event 
     */
    addMetric(event: MetricSelectorMetricClickedEvent) {
        let abbreviation = event.mAbr.trim();
        let metricFormulaString = `#[${abbreviation}]${event.mID}#`;

        // Close Metric Popup
        $(".metricSelectorOverlay").trigger("click");
        this.addToFormula(metricFormulaString, metricFormulaString.length);
    }

    /**
     * Uses the service and REST endpoint to validate the current formula.
     * Emits an event with the current state of the formula.  Used by Metric Definition to update its model.
     */
    async revalidate() {
        const isValid = await this.service.postValidateFormula(this.formulaString);
        this.isRootMetricBeingAddedToFormula = this.containsRootMetric(this.formulaString, this.rootMetricID);
        this.isValid = isValid && !this.isRootMetricBeingAddedToFormula;
        this.formulaChange?.emit(new FormulaState(this.formulaString, this.isValid));
        // Trigger change detection
        this.cdr.detectChanges();
    }

    /**
     * Validates if the Calculated Formula contains the Root Metric ID.
     */
    containsRootMetric(formulaString: string, metricID: number): boolean {
        const regex = /\](\d+)#/g;
        let match;
        while ((match = regex.exec(formulaString)) !== null) {
            if (Number(match[1]) === metricID) {
                return true; // The number exists between ] and #
            }
        }
        return false;
    }

    /**
     * Returns the value of the isValid boolean.
     */
    isValidFormula(): boolean {
        return this.isValid;
    }

    /**
     * Adds the "value" parameter to the current formula.
     * Called by the ngClick directives on the different math functions.
     * @param value Math function/operator or Metric ID to add to the formula
     * @param offset Offset of where to put the text edit cursor.
     */
    addToFormula(value: string, offset: number = 1) {
        let cursorStart = this.formulaTextArea.nativeElement.selectionStart;
        let cursorEnd = this.formulaTextArea.nativeElement.selectionEnd;
        if (!cursorEnd)
            cursorEnd = 0;

        //Append the substring
        this.formulaString = this.formulaString.substr(0, cursorStart) + value + this.formulaString.substr(cursorEnd);

        this.formulaTextArea.nativeElement.value = this.formulaString; //temp
        this.formulaTextArea.nativeElement.focus();
        let cursorPosition = cursorStart + offset;
        this.formulaTextArea.nativeElement.selectionStart = cursorPosition;
        this.formulaTextArea.nativeElement.selectionEnd = cursorPosition;
        this.revalidate();
    }
}
export default MetricFormulaComponent;

export class FormulaState {
    formulaString: string;
    isValid: boolean;

    constructor(formulaString: string, isValid: boolean) {
        this.formulaString = formulaString;
        this.isValid = isValid;
    }
}

type MetricSelectorMetricClickedEvent = { mAbr: string, mID: number };
